import type { EventPropertiesMap } from '@readme/iso';

import * as amplitude from '@amplitude/analytics-browser';
import { AMPLITUDE_EVENT, AMPLITUDE_EVENT_PROPERTY } from '@readme/iso/src/amplitude';
import { useCallback, useContext, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import type { ConfigContextValue, ProjectContextValue } from '@core/context';
import { ProjectContext, ConfigContext } from '@core/context';
import useUserPermissions from '@core/hooks/useUserPermissions';
import { useSuperHubStore } from '@core/store';

import { useSuperHubContext } from '@routes/SuperHub/components/Context';

import useSetupAmplitude from './useSetupAmplitude';

/**
 * Note:
 * We are no longer using Amplitude for page view tracking. No longer uses useLocation,
 * so testing does not need to use MemoryRouter.
 */

// Stable reference to an object with no-op functions to return when Amplitude is not enabled
const stableNoop = { setUserID: () => {}, track: () => {} };

export default function useAmplitude() {
  const { project } = useContext(ProjectContext) as ProjectContextValue;
  const [isSuperHub, slug, routeSection] = useSuperHubStore(s => [s.isSuperHub, s.slug, s.routeSection]);
  const history = useHistory();

  // Navigation sometimes fails if we are using regular history so we try to fetch the browserRouterHistory
  // from the SuperHub context. This is a workaround to avoid the issue. The router is only used in Amplitude
  // callbacks so its not critical, okay to use history from react-router-dom if it fails
  let browserRouterHistory;
  try {
    const superHubContext = useSuperHubContext();
    browserRouterHistory = superHubContext.browserRouterHistory;
  } catch {
    browserRouterHistory = history;
  }

  const { releaseVersion } = useContext(ConfigContext) as ConfigContextValue;
  const { isAdminUser } = useUserPermissions();

  // Setup Amplitude and keep track if it's enabled.
  const enabled = useSetupAmplitude();

  /**
   * NOTE: The setGroup calls are moved to the track function call, I don't really like this approach but its the
   * only way to consistently set group properties. Setting them 'globally' in the useEffect causes too much
   * requests, moving it to the track call reduces this, but it's not ideal.
   */

  // Core data included with every tracking event
  const eventMetaData = useMemo(() => {
    const { metrics } = project;

    return {
      ...(metrics && { metrics: { monthlyPurchaseLimit: metrics.monthlyPurchaseLimit } }),
      isSuperHub,
      permission: isAdminUser ? 'admin' : 'readonly',
      releaseVersion,
    };
  }, [isAdminUser, isSuperHub, project, releaseVersion]);

  // `amplitude.track` wrapper -> consume our core data, along with anything else passed in
  const track = useCallback(
    <T extends AMPLITUDE_EVENT>(
      event: T,
      eventProperties?: T extends keyof EventPropertiesMap ? EventPropertiesMap[T] : Record<string, unknown>,
    ) => {
      const subdomain = project.subdomain;
      const plan = project.plan;

      // Set groups for this track call
      amplitude.setGroup('project_id', project._id);
      amplitude.setGroup('subdomain', subdomain);
      amplitude.setGroup('plan', plan);

      return enabled && amplitude.track(event, { ...eventMetaData, ...eventProperties });
    },
    [enabled, eventMetaData, project._id, project.subdomain, project.plan],
  );

  // Set user ID for Amplitude client (i.e. in signup flows as it becomes known)
  const setUserID = useCallback((userID: string) => enabled && amplitude.setUserId(userID), [enabled]);

  // Register engagement callbacks after track is defined
  // Note: I believe we can only use window here since the Guides SDK does not provide any other way to access .engagement
  useEffect(() => {
    if (enabled && window.engagement) {
      window.engagement.addCallback('onBrandingClick', () => {
        window.location.hash = '/appearance/theme-editor';
      });

      window.engagement.addCallback('addContentClick', () => {
        track(AMPLITUDE_EVENT.PROJECT_EDIT_STARTED);
        const isEditableSection = ['changelog', 'docs', 'page', 'reference', 'recipes'].includes(routeSection ?? '');
        const entrySection = isEditableSection ? routeSection : 'docs';
        browserRouterHistory.push({
          pathname: `/update/${entrySection}${slug ? `/${slug}` : ''}`,
          hash: '#',
        });
      });

      window.engagement.addCallback('automagicAPIDocsClick', () => {
        browserRouterHistory.push({
          pathname: '/update/reference',
          hash: '#',
        });
      });
    }
  }, [enabled, track, browserRouterHistory, routeSection, slug]);

  // Only expose true Amplitude SDK methods if enabled
  if (!enabled) return stableNoop;

  // Expose core methods
  return { setUserID, track };
}

// Export event enums here as well for easy access when importing hook
export { AMPLITUDE_EVENT, AMPLITUDE_EVENT_PROPERTY };
